import { Game } from "../../core/Game";
import { benchAssembly, groupSeating } from "../../game/bench-work/assembly";
import { benchGroupAt } from "../../game/bench-work/bench-group";
import { productBlueprintFor } from "../../game/bench-work/blueprint";
import { selectedBenchPlan } from "../../game/bench-work/tool-work";
import {
  addConsumables,
  CONSUMABLE_TYPES,
  hasConsumables,
  subtractConsumables,
} from "../../game/Consumable";
import { playerAttendsMachine } from "../../game/machine-helpers";
import { isNight } from "../../game/time";
import { floorMachines, MachineEntity } from "../entities/MachineEntity";
import { projectPerson } from "../projection";
import { Clock } from "../singletons/Clock";
import { Consumables } from "../singletons/Consumables";
import { gatherBenchPieces } from "./bench-commands";
import { finishAttendedWork, operateMachine } from "./machine-commands";
import { emitSound } from "./sound";

/**
 * Blueprint assembly, one fastener at a time — the pallet's per-nail
 * commits run forward (docs/bench-work.md decision 4). Driving a
 * fastener spends exactly one out of the shop's supplies and records it
 * on the bench (`MachineState.assembly`); the last one claims the seated
 * parts and the piece comes together. Pulling a driven fastener back out
 * returns it to the supplies and frees the parts it held, so a build
 * started by mistake can be taken apart the way it went together. Every
 * press is real state: the count in the cabinet and the heads on the
 * bench never disagree, and a refresh mid-build finds every nail where
 * it was left.
 *
 * The bench view decides *when* (which head the hand is over); these
 * decide *what*, and refuse when there is nothing to spend, when the
 * parts a fastener joins aren't both on their outlines, or when the
 * drawing's driving tool isn't on this bench.
 */

function attends(game: Game, entity: MachineEntity): boolean {
  const person = projectPerson(game);
  return playerAttendsMachine(
    entity.view(),
    person.position,
    person.away !== null,
  );
}

/**
 * Drive the drawing's fastener `index` (into the blueprint's fastener
 * list) at the bench: one of the plan's fasteners leaves the supplies,
 * the head is recorded as driven, and the two parts it joins are nailed
 * on. The parts may lie across a run of tables; the drive gathers them
 * onto this one, since the build belongs to the bench with the drawing.
 * The last fastener commits the whole build in the same press.
 */
export function driveFastener(
  game: Game,
  entity: MachineEntity,
  index: number,
): boolean {
  const machineState = entity.state;
  if (machineState.operationProgress.status === "inProgress") {
    console.warn("The bench is mid-operation — nothing to fasten");
    return false;
  }
  if (!attends(game, entity)) {
    console.warn("Can't drive a fastener from across the shop");
    return false;
  }
  // Nothing new starts at night — and the last fastener starts the
  // build's own commit, which would refuse the same way
  if (isNight(game.entities.getSingleton(Clock))) {
    console.warn("Shop's closed for the night");
    return false;
  }
  const machine = entity.view();
  const plan = selectedBenchPlan(machine);
  const interaction = plan?.interaction;
  if (!plan || interaction?.kind !== "assembly") {
    console.warn("No drawing is out on this bench");
    return false;
  }
  const blueprint = productBlueprintFor(interaction.blueprint);
  if (!blueprint) {
    console.warn("The drawing names a build with no blueprint");
    return false;
  }
  // The build's operation resolves only with its driving tool on the
  // rail (a tool-borne plan lives on the tool's hook); the bench view
  // hangs the tool in hand before the first press.
  if (machine.selectedOperationOrNull?.id !== plan.id) {
    console.warn("The drawing's driving tool isn't on this bench");
    return false;
  }
  const fastener = blueprint.fasteners[index];
  if (!fastener) {
    console.warn("The drawing has no such fastener");
    return false;
  }
  const record = machineState.assembly;
  if (record && record.driven.length > 0 && record.planId !== blueprint.id) {
    console.warn("A different build is fastened together on this bench");
    return false;
  }
  if (record?.driven.includes(index)) {
    console.warn("That fastener is already driven");
    return false;
  }

  const group = benchGroupAt(floorMachines(game), machine);
  const { seated } = groupSeating(group, machine, blueprint);
  const [headSlot, pointSlot] = fastener.joins;
  const headPiece = seated.get(headSlot);
  const pointPiece = seated.get(pointSlot);
  if (!headPiece || !pointPiece) {
    console.warn("Both parts a fastener joins must lie on their outlines");
    return false;
  }

  const consumables = game.entities.getSingleton(Consumables);
  const cost = { id: blueprint.fastenerConsumable, amount: 1 };
  if (!hasConsumables(consumables.stock, [cost])) {
    console.warn(
      `Out of ${CONSUMABLE_TYPES[cost.id].name.toLowerCase()} — nothing to drive`,
    );
    return false;
  }

  // The parts a fastener joins lie on the bench that holds the build
  gatherBenchPieces(game, entity, [headPiece, pointPiece]);

  consumables.stock = subtractConsumables(consumables.stock, [cost]);
  game.dispatch("suppliesChanged", {});
  const driven = [...(record?.driven ?? []), index];
  entity.state = {
    ...entity.state,
    assembly: { planId: blueprint.id, driven },
  };
  game.dispatch("machineStateChanged", { machine: entity });

  if (driven.length >= blueprint.fasteners.length) {
    // The last fastener: every seated part onto this bench, then the
    // start and finish commits back to back — the hand work was the
    // build. The claim takes the very boards lying on the outlines.
    gatherBenchPieces(game, entity, [...seated.values()]);
    if (!operateMachine(game, entity) || !finishAttendedWork(game, entity)) {
      console.warn("The last fastener is in, but the build wouldn't commit");
    }
  }
  return true;
}

/**
 * Pull a driven fastener back out with the same tool: it returns to the
 * supplies and the parts it held come loose. The record empties with
 * the last one pulled, and the drawing can go back on the pile.
 */
export function pryFastener(
  game: Game,
  entity: MachineEntity,
  index: number,
): boolean {
  const machineState = entity.state;
  if (machineState.operationProgress.status === "inProgress") {
    console.warn("The bench is mid-operation — nothing to pull");
    return false;
  }
  if (!attends(game, entity)) {
    console.warn("Can't pull a fastener from across the shop");
    return false;
  }
  const build = benchAssembly(entity.view());
  if (!build || !build.drivenIndices.includes(index)) {
    console.warn("No such fastener is driven on this bench");
    return false;
  }
  const consumables = game.entities.getSingleton(Consumables);
  consumables.stock = addConsumables(consumables.stock, [
    { id: build.blueprint.fastenerConsumable, amount: 1 },
  ]);
  game.dispatch("suppliesChanged", {});
  const driven = build.drivenIndices.filter((driven) => driven !== index);
  const { assembly: _record, ...rest } = machineState;
  entity.state =
    driven.length > 0
      ? { ...rest, assembly: { planId: build.blueprint.id, driven } }
      : rest;
  game.dispatch("machineStateChanged", { machine: entity });
  emitSound(game, "nail-pry");
  return true;
}
